<!-- Measurement layer: invisible spans used only to measure each tab's natural width.
     Always rendered in tabs() input order so index mapping in _distribute() is correct. -->
@for (tab of tabs(); track tab.id) {
  <span #measureEl class="bn-tab-bar__measure" aria-hidden="true">
    @if (tab.icon) {
      <i [class]="tab.icon"></i>
    }
    {{ tab.label }}
    @if (tab.hasUpdate) {
      <span class="bn-tab-bar__update-dot"></span>
    }
  </span>
}

<!-- Tab bar row (overflow hidden to clip tabs, NOT the dropdown) -->
<div class="bn-tab-bar__row" role="tablist" aria-label="Content tabs">
  <!-- Inline visible tabs -->
  @for (tab of visibleTabs(); track tab.id) {
    <button
      type="button"
      role="tab"
      class="bn-tab-bar__tab"
      [class.bn-tab-bar__tab--active]="tab.id === activeTabId()"
      [class.bn-tab-bar__tab--temp]="tab.id === temporarilyVisibleTabId()"
      [attr.aria-selected]="tab.id === activeTabId()"
      [attr.tabindex]="tab.id === activeTabId() ? 0 : -1"
      [attr.aria-label]="(tab.id === temporarilyVisibleTabId() ? tab.label + ' (not pinned)' : tab.label) + (tab.hasUpdate ? ', new updates' : '')"
      (click)="selectTab(tab.id)"
      (keydown.enter)="selectTab(tab.id)"
      (keydown.space)="selectTab(tab.id); $event.preventDefault()"
    >
      @if (tab.icon) {
        <i [class]="tab.icon + ' bn-tab-bar__tab-icon'"></i>
      }
      <span>{{ tab.label }}</span>
      @if (tab.hasUpdate) {
        <span class="bn-tab-bar__update-dot" aria-hidden="true"></span>
      }
    </button>
  }

  <!-- More button (inside row for layout; dropdown is a sibling of the row) -->
  @if (overflowTabs().length > 0) {
    <button
      #moreBtn
      type="button"
      class="bn-tab-bar__more-btn"
      [class.bn-tab-bar__more-btn--has-update]="hasOverflowUpdates()"
      aria-haspopup="true"
      [attr.aria-expanded]="showOverflow()"
      title="More tabs. Pin tabs here to keep them in the main row."
      [attr.aria-label]="moreButtonAriaLabel()"
      (click)="toggleOverflow($event)"
      (keydown.enter)="toggleOverflow($event)"
      (keydown.space)="toggleOverflow($event); $event.preventDefault()"
    >
      More
      @if (hasOverflowUpdates()) {
        <span class="bn-tab-bar__update-dot" aria-hidden="true"></span>
      }
      <i class="pi pi-chevron-down bn-tab-bar__more-icon" [class.bn-tab-bar__more-icon--open]="showOverflow()"></i>
    </button>
  }
</div>

<!-- Overflow dropdown — sibling of the row, positioned relative to :host, NOT clipped by row overflow.
     Lists ALL tabs (not just overflow) so users can manage pin state for any tab. -->
@if (overflowTabs().length > 0 && showOverflow()) {
  <div
    class="bn-tab-bar__dropdown"
    role="menu"
    aria-label="Additional tabs"
    (click)="$event.stopPropagation()"
  >
    @for (tab of moreMenuTabs(); track tab.id) {
      <div class="bn-tab-bar__dropdown-row">
        <!-- Tab navigation button — navigates to the tab and closes the menu -->
        <button
          type="button"
          role="menuitem"
          class="bn-tab-bar__dropdown-item"
          [class.bn-tab-bar__dropdown-item--active]="tab.id === activeTabId()"
          [attr.aria-current]="tab.id === activeTabId() ? 'page' : null"
          (click)="selectTab(tab.id)"
          (keydown.enter)="selectTab(tab.id)"
          (keydown.space)="selectTab(tab.id); $event.preventDefault()"
        >
          @if (tab.icon) {
            <i [class]="tab.icon + ' bn-tab-bar__dropdown-icon'"></i>
          }
          <span class="bn-tab-bar__dropdown-label">{{ tab.label }}</span>
          @if (tab.hasUpdate) {
            <span class="bn-tab-bar__update-dot" aria-hidden="true"></span>
          }
          @if (tab.id === activeTabId()) {
            <i class="pi pi-check bn-tab-bar__dropdown-check"></i>
          }
        </button>

        <!-- Pin control — only shown for pin-eligible (non-mandatory) tabs -->
        @if (isMandatory(tab.id)) {
          <!-- Mandatory tabs: non-interactive lock indicator -->
          <span
            class="bn-tab-bar__pin-badge"
            aria-disabled="true"
            [attr.aria-label]="'Required tab: ' + tab.label"
            title="Required tab — always pinned"
          >
            <i class="pi pi-lock bn-tab-bar__pin-icon bn-tab-bar__pin-icon--locked"></i>
          </span>
        } @else {
          <!-- Optional tabs: toggle pin button — does NOT close the menu -->
          <button
            type="button"
            role="menuitemcheckbox"
            class="bn-tab-bar__pin-btn"
            [class.bn-tab-bar__pin-btn--pinned]="isPinned(tab.id)"
            [attr.aria-checked]="isPinned(tab.id)"
            [attr.aria-label]="isPinned(tab.id) ? 'Unpin ' + tab.label : 'Pin ' + tab.label"
            [title]="isPinned(tab.id) ? 'Unpin ' + tab.label + ' from main row' : 'Pin ' + tab.label + ' to main row'"
            (click)="togglePin(tab.id, $event)"
            (keydown.enter)="togglePin(tab.id, $event)"
            (keydown.space)="togglePin(tab.id, $event); $event.preventDefault()"
          >
            <i
              class="pi bn-tab-bar__pin-icon"
              [class.pi-bookmark]="!isPinned(tab.id)"
              [class.pi-bookmark-fill]="isPinned(tab.id)"
            ></i>
          </button>
        }
      </div>
    }
  </div>
}
